// @ts-check
/**
 * @typedef {{ success: true }} SubmitSuccess
 * @typedef {{ success: false, errors: Object<string, string> }} SubmitValidationError
 * @typedef {{ success: false, error: 'turnstile_failed' }} SubmitTurnstileError
 * @typedef {SubmitSuccess|SubmitValidationError|SubmitTurnstileError} SubmitResponse
 * @typedef {{ send: string, sending: string, turnstileError: string, turnstileRetry: string, submitError: string }} ContactFormStrings
 */

class ContactForm extends window.BAO.CustomElement() {
  static get requiredElements () {
    return ['form', 'success', 'errors', 'errorList', 'submit', 'submitLabel']
  }

  /** @returns {ContactFormStrings} */
  static get strings () {
    const theme = /** @type {any} */ (window.theme)
    return theme.strings.contactForm
  }

  setupListeners () {
    super.setupListeners()

    this.listeners.add(
      this.els.form.element,
      'submit',
      this.onFormSubmit.bind(this),
    )
  }

  /** @returns {string|undefined} */
  get endpoint () {
    return this.dataset.contactFormEndpoint
  }

  /** @returns {HTMLFormElement} */
  get form () {
    return /** @type {HTMLFormElement} */ (this.els.form.element)
  }

  /** @returns {string} */
  get turnstileToken () {
    const input = /** @type {HTMLInputElement|null} */ (this.form.querySelector(
      '[name="cf-turnstile-response"]',
    ))

    return input ? input.value : ''
  }

  resetTurnstile () {
    if (!this.els.turnstile?.exists || !window.turnstile) return

    window.turnstile.reset(this.els.turnstile.element)
  }

  /** @returns {boolean} */
  get busy () {
    return this.getAttribute('aria-busy') === 'true'
  }

  /** @param {boolean} busy */
  set busy (busy) {
    const submit = /** @type {HTMLButtonElement} */ (this.els.submit.element)

    this.setAttribute('aria-busy', String(busy))
    submit.disabled = busy
    this.els.submitLabel.element.textContent = busy
      ? ContactForm.strings.sending
      : ContactForm.strings.send
  }

  hideStatus () {
    this.els.success.element.setAttribute('aria-hidden', 'true')
    this.els.errors.element.setAttribute('aria-hidden', 'true')
  }

  showSuccess () {
    this.hideStatus()
    this.els.success.element.setAttribute('aria-hidden', 'false')
    this.form.reset()
  }

  /** @param {string[]} messages */
  showErrors (messages) {
    this.hideStatus()

    this.els.errorList.element.innerHTML = ''
    messages.forEach(message => {
      const item = document.createElement('li')
      item.textContent = message
      this.els.errorList.element.appendChild(item)
    })

    this.els.errors.element.setAttribute('aria-hidden', 'false')
  }

  /** @param {SubmitEvent} e */
  async onFormSubmit (e) {
    e.preventDefault()

    if (!this.endpoint || !this.form.reportValidity()) return

    if (this.els.turnstile?.exists && !this.turnstileToken) {
      this.showErrors([ContactForm.strings.turnstileError])
      return
    }

    const formData = new FormData(this.form)
    const payload = {
      customer_email: formData.get('customer_email'),
      customer_firstname: formData.get('customer_firstname'),
      customer_lastname: formData.get('customer_lastname'),
      order_number: formData.get('order_number'),
      message: formData.get('message'),
      request_type: formData.get('request_type'),
    }

    if (this.els.turnstile?.exists) {
      payload.turnstile_token = this.turnstileToken
    }

    this.busy = true

    try {
      const response = await fetch(this.endpoint, {
        method: 'POST',
        mode: 'cors',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      })

      /** @type {SubmitResponse|null} */
      const data = await response.json().catch(() => null)

      if (response.ok && data?.success) {
        this.showSuccess()
      } else if (
        data &&
        data.success === false &&
        'error' in data &&
        data.error === 'turnstile_failed'
      ) {
        this.showErrors([ContactForm.strings.turnstileRetry])
      } else if (data && data.success === false && 'errors' in data) {
        this.showErrors(Object.values(data.errors))
      } else {
        this.showErrors([ContactForm.strings.submitError])
      }
    } catch (error) {
      console.error(error)
      this.showErrors([ContactForm.strings.submitError])
    } finally {
      this.busy = false
      this.resetTurnstile()
    }
  }
}

if (!customElements.get('contact-form')) {
  customElements.define('contact-form', ContactForm)
}
